/**
 * A milestone, celebrated once (SPEC §12).
 *
 * What is celebrated is a competency reaching `acquis` or `consolidé` — the
 * thing the project is actually after. Not a streak: a streak rewards
 * connecting and punishes a day off sick, and a broken streak demotivates more
 * than it ever motivated.
 *
 * **Deux sources, un seul dessin.** En séance, le palier arrive par le flux
 * d'événements ; hors séance (« Mon travail », révision, série flash), il arrive
 * dans la réponse de la correction (`CorrectionOut.jalon`, constaté par
 * `mastery` au moment de l'observation). `MilestoneNotice` est le bandeau
 * lui-même, que les deux chemins affichent à l'identique : un palier franchi
 * le soir ne vaut pas moins qu'un palier franchi avec le tuteur.
 */

import { useEffect, useState } from "react";

import type { SessionEvent } from "@/api/session-stream";
import { MILESTONE_SENTENCE, isMasteryState, isMilestone } from "@/config/wording";

type Milestone = { key: string; competency: string; sentence: string };

function readMilestone(event: SessionEvent): Milestone | null {
  const payload = event.payload;
  const state = payload.state;
  const previous = payload.previous_state;
  if (!isMasteryState(state) || !isMasteryState(previous)) return null;
  if (!isMilestone(previous, state)) return null;
  if (state !== "acquis" && state !== "consolide") return null;
  return {
    key: `${event.id}`,
    competency: typeof payload.competency_label === "string" ? payload.competency_label : "",
    sentence: MILESTONE_SENTENCE[state],
  };
}

export function MilestoneBanner({ corrections }: { corrections: SessionEvent[] }) {
  const [shown, setShown] = useState<Milestone | null>(null);

  useEffect(() => {
    const latest = [...corrections].reverse().map(readMilestone).find(Boolean) ?? null;
    if (!latest) return;
    setShown(latest);
    const timer = window.setTimeout(() => setShown(null), 6_000);
    return () => window.clearTimeout(timer);
  }, [corrections]);

  if (!shown) return null;

  return <MilestoneNotice competency={shown.competency} sentence={shown.sentence} />;
}

/** Le bandeau, sans sa source : un intitulé et la phrase de l'état atteint. */
export function MilestoneNotice({
  competency,
  sentence,
}: {
  competency: string;
  sentence: string;
}) {
  return (
    <div className="milestone" role="status">
      <span className="milestone__mark" aria-hidden="true">
        ✦
      </span>
      <p>
        {competency ? <strong>{competency}</strong> : null} {sentence}
      </p>
    </div>
  );
}
